<template>
  <div class="bulk-order-items-index">
    <div class="feedback">
      <transition name="fade" @enter="enterErrorFeedback">
        <feedback-error v-if="showableErrorFeedback" :message="errorMessage" />
      </transition>
    </div>
    <SearchFormHeader
      :filter-count="filterCount"
      :sort-options="itemSortOptions"
      :sort-selected-value="sortSelectedValue"
      :total-count="totalCount"
      :is-loading="isShowTotalCountLoading"
      @click-filter="showSearchForm"
      @change-sort="onSelectSort"
    />
    <div class="search-result">
      <div v-if="isLoadingItems" class="loading-section">
        <Loading />
      </div>
      <div v-else class="search-result__loaded">
        <div v-if="isNotEmptyResult">
          <div class="search-result__items">
            <ItemBoxesConnected
              :items="items"
              @click-item-boxes-connected="onClickItem"
            />
          </div>
          <div class="search-result__pagination">
            <Pagination
              class="pagination"
              :current-page="currentPage"
              :total-pages="totalPages"
              @clicked="onClickPageLink"
            />
          </div>
        </div>
        <div v-else-if="isLoadedItems" class="empty-result">
          <div class="body">条件に一致するギフトは見つかりませんでした</div>
        </div>
      </div>
    </div>
    <SearchForm
      :showable-search-form="showableSearchForm"
      :keyword="getCurrentInputtedKeyword()"
      :categories="categories"
      :higher-budget="higherBudget"
      :lower-budget="lowerBudget"
      :scenes="scenes"
      :receiving-kinds="receivingKinds"
      :selected-category-ids="selectedCategoryIds"
      :selected-scene-ids="selectedSceneIds"
      :selected-receiving-kind-ids="selectedReceivingKindIds"
      @hide="hideSearchForm"
      @submit="onSubmitSearchForm"
    />
    <IndexPageRecentViewSection
      :need-list-link="false"
      :hide-campaign="true"
      item-link-base-path="/bulk_order"
      :item-link-params="{ token: giftCardSessionToken }"
      @click-item-box="onClickRecentViewItem"
    />
    <div v-if="showEmptyResultContents" class="search-result--empty">
      <IndexPageSceneListSection
        class="scene"
        :need-list-link="false"
        item-link-base-path="/bulk_order"
        :item-link-params="{ token: giftCardSessionToken }"
        @click-scene="onClickScene"
      />
      <IndexPageCategoriesSection
        item-link-base-path="/bulk_order"
        :item-link-params="{ token: giftCardSessionToken }"
        @error="onSectionError"
        @click-category="onClickCategory"
      />
    </div>
    <SearchItemBottomLinks
      class="bulk-order-items-index__bottom-link"
      :back-to-gift-card-edit-url="backToGiftCardEditPage"
    />
  </div>
</template>

<script>
import {
  itemSorts,
  itemSortOptions
} from '../../../../constants/itemSearchConstatnts.js'
import axios from 'axios'
import qs from 'qs'
import url from 'url'
// NOTE: ネストしたクエリパラメータをパースしてくれる
// ref: https://github.com/ljharb/qs
// ref: https://tech-1natsu.hatenablog.com/entry/2018/07/04/175214
import Pagination from '../../../organism/paginations/Pagination.vue'
import SearchFormHeader from '../../../organism/search_forms/SearchFormHeader.vue'
import SearchForm from '../../../organism/search_forms/SearchForm.vue'
import ItemBoxesConnected from '../../../molecules/item_boxes/ItemBoxesConnected.vue'
import Loading from '../../../atoms/loadings/Loading.vue'
import FeedbackError from '../../../atoms/feedbacks/feedback-error.vue'
import SearchItemsQuery from '../../../../../../graphqls/public/queries/search_items'
import IndexPageRecentViewSection from '../../../organism/homes/sections/IndexPageRecentViewSection.vue'
import IndexPageSceneListSection from '../../../organism/homes/sections/IndexPageSceneListSection.vue'
import IndexPageCategoriesSection from '../../../organism/homes/sections/IndexPageCategoriesSection.vue'
import SearchItemBottomLinks from '../../../organism/bulk_personal_orders/bottom_links/SearchItemBottomLinks.vue'
import SearchedItemForBulkPersonalOrder from '../../../../utils/searched-item-for-bulk-personal-order.js'
import * as Sentry from '@sentry/browser'
import { bulkOrderAgreementChecker } from '../../../../pages/bulk_order/common/bulkOrderAgreementChecker.js'

const { RECOMMENDED } = itemSorts

export default {
  components: {
    SearchFormHeader,
    SearchForm,
    Pagination,
    ItemBoxesConnected,
    Loading,
    FeedbackError,
    IndexPageRecentViewSection,
    IndexPageSceneListSection,
    IndexPageCategoriesSection,
    SearchItemBottomLinks
  },
  props: {
    bulkPersonalOrderSessionId: {
      type: Number,
      required: true
    },
    giftCardSessionToken: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      items: [],
      categories: [],
      scenes: [],
      receivingKinds: [],
      sortSelectedValue: RECOMMENDED.key,
      itemSortOptions,
      isFilterActive: false,
      showableSearchForm: false,
      isLoadingItems: false,
      isLoadingTotalCount: false,
      isLoadedItems: false,
      isAllCategoryChecked: false,
      isAllSceneChecked: false,
      lowerBudget: null,
      higherBudget: null,
      keyword: '',
      selectedCategoryIds: [],
      selectedSceneIds: [],
      selectedReceivingKindIds: [],
      currentPage: 1,
      totalPages: 1,
      totalCount: null,
      totalItemCountOnSearchForm: null,
      showableErrorFeedback: false,
      errorMessage: ''
    }
  },
  computed: {
    showEmptyResultContents() {
      return !this.isNotEmptyResult && this.isLoadedItems
    },
    isNotEmptyResult() {
      return this.items.length !== 0
    },
    isShowTotalCountLoading() {
      return this.isLoadingTotalCount || this.totalCount === null
    },
    hittedItemCount() {
      return this.totalItemCountOnSearchForm !== null
        ? this.totalItemCountOnSearchForm
        : this.items.length
    },
    isLowerBudgetActive() {
      return this.lowerBudget !== null
    },
    isHigherBudgetActive() {
      return this.higherBudget !== null
    },
    isFiltered() {
      return (
        this.isLowerBudgetActive ||
        this.isHigherBudgetActive ||
        this.selectedCategoryIds.length > 0 ||
        this.selectedSceneIds.length > 0 ||
        this.selectedReceivingKindIds.length > 0
      )
    },
    filterCount() {
      return (
        this.selectedCategoryIds.length +
        this.selectedSceneIds.length +
        this.selectedReceivingKindIds.length +
        (this.isLowerBudgetActive ? 1 : 0) +
        (this.isHigherBudgetActive ? 1 : 0)
      )
    },
    backToGiftCardEditPage() {
      return this.giftCardSessionToken === 'new'
        ? `/bulk_order/gift_cards/new`
        : `/bulk_order/gift_cards/${this.giftCardSessionToken}/edit`
    }
  },
  beforeCreate() {
    // まとめてオーダーの注意事項に同意していない場合は、ギフトカードリストへ戻す
    bulkOrderAgreementChecker()
  },
  async mounted() {
    this.initializeParams()
    await this.getItemCategories()
    await this.getScenes()
    await this.getItemReceivingKinds()
    await this.search(this.currentPage, true)

    // NOTE: ブラウザバック・フォワード時の更新処理
    window.onpopstate = async function () {
      this.initializeParams()
      await this.search(this.currentPage, true)
    }.bind(this)
  },
  methods: {
    onClickItem(itemId) {
      dataLayer.push({
        event: 'click_bulk_item_list__item_detail',
        item_id: itemId,
        sort_key: this.sortSelectedValue,
        lower_budget: this.lowerBudget,
        higher_budget: this.higherBudget,
        scene_id:
          this.selectedSceneIds.length > 0
            ? this.selectedSceneIds.join(',')
            : '',
        category_id:
          this.selectedCategoryIds.length > 0
            ? this.selectedCategoryIds.join(',')
            : '',
        item_receiving_kinds:
          this.selectedReceivingKindIds.length > 0
            ? this.selectedReceivingKindIds.join(',')
            : '',
        keyword: this.keyword,
        bulk_personal_order_session_id: this.bulkPersonalOrderSessionId
      })
    },
    getCurrentInputtedKeyword() {
      return document.querySelector('.search-form__input--text').value
    },
    onSectionError(message) {
      this.errorMessage = message
      this.showableErrorFeedback = true
    },
    initializeParams() {
      const urlQuery = url.parse(location.href).query
      if (!urlQuery) {
        this.sortSelectedValue = RECOMMENDED.key
        this.keyword = ''
        this.lowerBudget = null
        this.higherBudget = null
        this.selectedCategoryIds = []
        this.selectedSceneIds = []
        this.selectedReceivingKindIds = []
        this.currentPage = 1
        return
      }
      const parsed = qs.parse(urlQuery)
      this.sortSelectedValue = parsed.sort_key
        ? parsed.sort_key
        : RECOMMENDED.key
      this.keyword = parsed.keyword ? parsed.keyword : ''
      this.lowerBudget = parsed.lower_budget
        ? Number(parsed.lower_budget)
        : null
      this.higherBudget = parsed.higher_budget
        ? Number(parsed.higher_budget)
        : null
      this.selectedCategoryIds = parsed.category_ids
        ? parsed.category_ids.map(categoryId => Number(categoryId))
        : []
      this.selectedSceneIds = parsed.scene_ids
        ? parsed.scene_ids.map(sceneId => Number(sceneId))
        : []
      this.selectedReceivingKindIds =
        parsed.item_receiving_kinds && parsed.item_receiving_kinds
          ? parsed.item_receiving_kinds.map(itemReceivingKindId =>
              Number(itemReceivingKindId)
            )
          : []
      this.currentPage = parsed.page_number ? Number(parsed.page_number) : 1
    },
    async onSelectSort(value) {
      this.sortSelectedValue = value
      this.keyword = this.getCurrentInputtedKeyword()
      this.scrollToTop()
      await this.search(1)
      this.updateUrlFromParams()
      this.dispatchDoSearchEvent()
    },
    showSearchForm() {
      this.showableSearchForm = true
    },
    hideSearchForm() {
      this.showableSearchForm = false
    },
    async onClickPageLink(page) {
      this.scrollToTop()
      await this.search(page)
      this.updateUrlFromParams()
    },
    async onSubmitSearchForm(
      inputtedLowerBudget,
      inputtedHigherBudget,
      inputtedSelectedCategoryIds,
      inputtedSelectedSceneIds,
      inputtedSelectedReceivingKindIds
    ) {
      this.hideSearchForm()
      this.keyword = this.getCurrentInputtedKeyword()
      this.lowerBudget = inputtedLowerBudget
      this.higherBudget = inputtedHigherBudget
      this.selectedCategoryIds = inputtedSelectedCategoryIds
      this.selectedSceneIds = inputtedSelectedSceneIds
      this.selectedReceivingKindIds = inputtedSelectedReceivingKindIds
      await this.search(1, true)
      this.updateUrlFromParams()
      this.dispatchDoSearchEvent()
    },
    dispatchDoSearchEvent() {
      dataLayer.push({
        event: 'do__search_bulk',
        sort_key: this.sortSelectedValue,
        lower_budget: this.lowerBudget,
        higher_budget: this.higherBudget,
        scene_id:
          this.selectedSceneIds.length > 0
            ? this.selectedSceneIds.join(',')
            : '',
        category_id:
          this.selectedCategoryIds.length > 0
            ? this.selectedCategoryIds.join(',')
            : '',
        item_receiving_kinds:
          this.selectedReceivingKindIds.length > 0
            ? this.selectedReceivingKindIds.join(',')
            : '',
        keyword: this.keyword,
        via: 'item_list',
        bulk_personal_order_session_id: this.bulkPersonalOrderSessionId
      })
    },
    createParamsObjectFromData(page) {
      const targetPageNumber = page ? page : this.currentPage
      const token = { token: this.giftCardSessionToken }
      const keyword = this.keyword ? { keyword: this.keyword } : {}
      const sortKey =
        this.sortSelectedValue !== RECOMMENDED.key
          ? { sort_key: this.sortSelectedValue }
          : {}
      const lowerBudget = this.lowerBudget
        ? { lower_budget: this.lowerBudget }
        : {}
      const higherBudget = this.higherBudget
        ? { higher_budget: this.higherBudget }
        : {}
      const categoryIds = this.selectedCategoryIds
        ? { category_ids: this.selectedCategoryIds }
        : {}
      const sceneIds = this.selectedSceneIds
        ? { scene_ids: this.selectedSceneIds }
        : {}
      const itemReceivingKinds = this.selectedReceivingKindIds
        ? { item_receiving_kinds: this.selectedReceivingKindIds }
        : {}
      const pageNumber = { page_number: targetPageNumber }
      return Object.assign(
        {},
        sortKey,
        lowerBudget,
        higherBudget,
        categoryIds,
        sceneIds,
        itemReceivingKinds,
        pageNumber,
        keyword,
        token
      )
    },
    updateUrlFromParams() {
      const params = this.createParamsObjectFromData()
      if (params.page_number === 1) {
        delete params.page_number
      }
      const paramsString = qs.stringify(params, { arrayFormat: 'brackets' })
      history.pushState(
        paramsString,
        '',
        !paramsString
          ? '/bulk_order/items'
          : `/bulk_order/items?${paramsString}`
      )
    },
    async search(pageNumber, isLoadingTotalCount = false) {
      this.items = []
      this.isFilterActive = this.isFiltered
      this.isLoadingItems = true
      this.isLoadedItems = false
      if (isLoadingTotalCount) this.isLoadingTotalCount = true
      try {
        const response = await this.$apollo.provider.clients.publicClient.query(
          {
            query: SearchItemsQuery,
            variables: {
              keyword: this.keyword,
              pageNumber: pageNumber,
              itemPerPage: 30,
              itemReceivingKindIds: this.selectedReceivingKindIds,
              sortKey: this.sortSelectedValue,
              sceneIds: this.selectedSceneIds,
              categoryIds: this.selectedCategoryIds,
              stringLowerBudget:
                this.lowerBudget != null
                  ? this.lowerBudget.toString()
                  : this.lowerBudget,
              stringHigherBudget:
                this.higherBudget != null
                  ? this.higherBudget.toString()
                  : this.higherBudget
            }
          }
        )

        const { currentPage, totalPages, totalCount } =
          response.data.searchItems.pagination
        this.currentPage = currentPage
        this.totalPages = totalPages
        this.totalCount = totalCount
        this.items = response.data.searchItems.items.map(item =>
          SearchedItemForBulkPersonalOrder.createSearchedItemObject(item, {
            token: this.giftCardSessionToken
          })
        )
      } catch (error) {
        Sentry.captureException(error)
        this.showableErrorFeedback = true
        this.errorMessage =
          'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
      this.isLoadingItems = false
      this.isLoadedItems = true
      if (isLoadingTotalCount) this.isLoadingTotalCount = false
    },
    async getItemCategories() {
      try {
        const res = await axios.get('/api/v1/pickup/item_categories')
        this.categories = res.data.data.map(item_category => {
          return {
            id: item_category.attributes.id,
            name: item_category.attributes.name
          }
        })
      } catch (error) {
        this.isLoadingItems = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    async getScenes() {
      try {
        const res = await axios.get('/api/v1/pickup/scenes')
        this.scenes = res.data.data.map(scene => {
          return {
            id: scene.attributes.id,
            name: scene.attributes.name,
            description: scene.attributes.description,
            imageUrl: scene.attributes.image_url
          }
        })
      } catch (error) {
        this.isLoadingItems = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    async getItemReceivingKinds() {
      try {
        const res = await axios.get('/api/v1/pickup/item_receiving_kinds')
        this.receivingKinds = res.data.data.map(receivingKind => {
          return {
            id: receivingKind.attributes.id,
            name: receivingKind.attributes.displayName
          }
        })
      } catch (error) {
        this.isLoadingItems = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    scrollToTop() {
      window.scrollTo({ top, behavior: 'smooth' })
    },
    enterErrorFeedback() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    },
    onClickRecentViewItem(itemId) {
      dataLayer.push({
        event: 'click_bulk_view_history__item',
        bulk_personal_order_session_id: this.bulkPersonalOrderSessionId,
        item_id: itemId
      })
    },
    onClickScene(sceneId) {
      dataLayer.push({
        event: 'do__search_bulk',
        sort_key: RECOMMENDED.key,
        lower_budget: null,
        higher_budget: null,
        scene_id: sceneId.toString(),
        category_id: '',
        item_receiving_kinds: '',
        keyword: '',
        via: 'item_list',
        bulk_personal_order_session_id: this.bulkPersonalOrderSessionId
      })
    },
    onClickCategory(categoryId) {
      dataLayer.push({
        event: 'do__search_bulk',
        sort_key: RECOMMENDED.key,
        lower_budget: null,
        higher_budget: null,
        scene_id: '',
        category_id: categoryId.toString(),
        item_receiving_kinds: '',
        keyword: '',
        via: 'item_list',
        bulk_personal_order_session_id: this.bulkPersonalOrderSessionId
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.search-result {
  &__items {
    margin-bottom: 16px;
    background-color: $white;

    @media all and (max-width: ($base-contents-width - 1)) {
      margin-bottom: 1px;
    }
  }

  &__pagination {
    margin-bottom: 16px;
    background-color: $gray-3;

    @media all and (max-width: ($base-contents-width - 1)) {
      background-color: $white;
    }

    .pagination {
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  .item-boxes--connected {
    // NOTE: _base.scssでbodyに対してtext-align:centerが指定されていることへの対応
    text-align: left;
    justify-content: center;
    max-width: $max-contents-width;
    margin: 0 auto;
  }

  .loading-section {
    text-align: center;
    background-color: $white;
    // height 29px で ローディングの下端の高さになる
    height: 29px;
    // padding-top 12px でローディングの上端の高さになるので、 12 + 24 = 52px でローディングの上端から40pxのpaddingになる。
    padding-top: 52px;
    padding-bottom: 40px;
  }

  .empty-result {
    background-color: $white;
    padding-top: 64px;
    padding-bottom: 64px;
  }
}
.search-result--empty {
  .scene {
    max-width: none;
    width: auto;
  }
}
.bulk-order-items-index {
  &__bottom-link {
    padding: 24px;
  }
}
</style>
